// src/pages/SmsCampaigns.tsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Users, MessageSquare, Target, 
  Plus, Filter, Send, Calendar, RefreshCw, 
  Eye, Edit, Trash2, TrendingUp, AlertCircle
} from 'lucide-react';
import { CreateCampaignModal } from '../components/CreateCampaignModal';
import toast from 'react-hot-toast';
import { API_BASE_URL, getAspirantId, testCampaignEndpoint, getAspirantCampaigns } from '../services/api';

// Define the campaign type matching your backend DTO
interface CampaignDTO {
  id: number;
  campaignName: string;
  status: 'DRAFT' | 'PUBLISHED' | 'SCHEDULED' | 'SMS_SENT' | 'COMPLETED' | 'ARCHIVED';
  type: 'MANIFESTO' | 'STRATEGY' | 'UPDATE' | 'EVENT' | 'ACHIEVEMENT' | 'SURVEY' | 'RESPONSE';
  aspirantName: string;
  aspirantId: number;
  estimatedRecipients: number;
  actualRecipients: number;
  smsSent: boolean;
  smsRecipients: number;
  publishedAt: string | null;
  scheduledAt: string | null;
  createdAt: string;
  summary: string;
  content: string;
  targetWardIds: number[];
  description?: string;
  documentUrl?: string;
  videoUrl?: string;
  imageUrl?: string;
  feedbackLink?: string;
}

export const SmsCampaigns: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignDTO[]>([]);
  const [aspirantId, setAspirantId] = useState<number | null>(null);
  const [aspirantInfo, setAspirantInfo] = useState<any>(null);
  const [apiWorking, setApiWorking] = useState<boolean | null>(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalCampaigns: 0,
    published: 0,
    draft: 0,
    scheduled: 0,
    smsSent: 0,
    citizensReached: 0,
  });

  // Get aspirant ID on component mount
  useEffect(() => {
    const id = getAspirantId();
    setAspirantId(id);
    
    // Test API connectivity
    testApiConnection();
    
    if (id) {
      fetchAspirantAndCampaigns(id);
    }
  }, []);

  // Test API connection
  const testApiConnection = async () => {
    try {
      const isWorking = await testCampaignEndpoint();
      setApiWorking(isWorking);
      console.log('Campaign API test:', isWorking ? '✅ Working' : '❌ Not working');
    } catch (error) {
      console.error('API test failed:', error);
      setApiWorking(false);
    }
  };

  // Fetch aspirant info and campaigns
  const fetchAspirantAndCampaigns = async (aspirantId: number) => {
    setLoading(true);
    try {
      // Fetch aspirant with ward info
      await fetchAspirantInfo(aspirantId);
      
      // Fetch campaigns
      await fetchCampaigns(aspirantId);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch aspirant info
  const fetchAspirantInfo = async (aspirantId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/aspirants/${aspirantId}/with-ward`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAspirantInfo(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching aspirant info:', error);
    }
  };

  // Fetch campaigns with improved error handling
  const fetchCampaigns = async (aspirantId: number) => {
    try {
      const token = localStorage.getItem('token');
      
      // Use correct URL path - no double /api
      const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/aspirant/${aspirantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('aspirant');
        setTimeout(() => window.location.href = '/login', 2000);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setCampaigns(data.data);
        
        // Calculate dashboard stats
        const totalCampaigns = data.data.length;
        const published = data.data.filter((c: CampaignDTO) => c.status === 'PUBLISHED').length;
        const draft = data.data.filter((c: CampaignDTO) => c.status === 'DRAFT').length;
        const scheduled = data.data.filter((c: CampaignDTO) => c.status === 'SCHEDULED').length;
        const smsSent = data.data.filter((c: CampaignDTO) => c.smsSent).length;
        const citizensReached = data.data.reduce((sum: number, c: CampaignDTO) => 
          sum + (c.actualRecipients || c.smsRecipients || 0), 0);
        
        setDashboardStats({
          totalCampaigns,
          published,
          draft,
          scheduled,
          smsSent,
          citizensReached,
        });
        
        console.log(`✅ Loaded ${totalCampaigns} campaigns`);
      } else {
        toast.error(data.error || 'Failed to fetch campaigns');
      }
    } catch (error) {
      console.error('❌ Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    }
  };

  useEffect(() => {
    // Listen for campaign creation events
    const handleCampaignCreated = () => {
      if (aspirantId) {
        fetchCampaigns(aspirantId);
      }
    };
    
    window.addEventListener('campaign-created', handleCampaignCreated);
    
    return () => {
      window.removeEventListener('campaign-created', handleCampaignCreated);
    };
  }, [aspirantId]);

  // Map status to frontend display
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'DRAFT': return { label: 'Draft', color: 'bg-gray-100 text-gray-800' };
      case 'PUBLISHED': return { label: 'Published', color: 'bg-[#0b2045]/10 text-[#0b2045]' };
      case 'SCHEDULED': return { label: 'Scheduled', color: 'bg-[#fefce8] text-[#ca8a04]' };
      case 'SMS_SENT': return { label: 'SMS Sent', color: 'bg-[#3c8d2e]/10 text-[#3c8d2e]' };
      case 'COMPLETED': return { label: 'Completed', color: 'bg-[#0b2045]/5 text-[#0b2045]' };
      case 'ARCHIVED': return { label: 'Archived', color: 'bg-gray-100 text-gray-800' };
      default: return { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    }
  };

  // Get type display name
  const getTypeDisplay = (type: string) => {
    const typeMap: Record<string, string> = {
      'MANIFESTO': 'Manifesto',
      'STRATEGY': 'Strategy',
      'UPDATE': 'Update',
      'EVENT': 'Event',
      'ACHIEVEMENT': 'Achievement',
      'SURVEY': 'Survey',
      'RESPONSE': 'Response'
    };
    return typeMap[type] || type;
  };

  // Calculate cost for display
  const calculateCost = (campaign: CampaignDTO) => {
    const recipients = campaign.smsRecipients || campaign.estimatedRecipients || 0;
    const cost = recipients * 0.65;
    return {
      cost: cost.toLocaleString('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
      recipients
    };
  };

  // Filter campaigns based on selected filter
  const filteredCampaigns = filter === 'all' 
    ? campaigns
    : campaigns.filter(campaign => campaign.status === filter);

  // Handle campaign actions
  const handleViewCampaign = (campaignId: number) => {
    console.log('View campaign:', campaignId);
    toast('View campaign feature coming soon', {
      icon: '👁️',
      duration: 3000
    });
  };

  const handleEditCampaign = (campaignId: number) => {
    console.log('Edit campaign:', campaignId);
    toast('Edit campaign feature coming soon', {
      icon: '✏️',
      duration: 3000
    });
  };

  const handleDeleteCampaign = async (campaignId: number) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        const token = localStorage.getItem('token');
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/${campaignId}`, {
          method: 'DELETE',
          headers,
        });
        
        if (response.ok) {
          toast.success('Campaign deleted successfully');
          if (aspirantId) fetchCampaigns(aspirantId);
        } else {
          if (response.status === 401) {
            toast.error('Session expired. Please login again.');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return;
          }
          const error = await response.json();
          toast.error(`Failed to delete: ${error.error}`);
        }
      } catch (error) {
        toast.error('Error deleting campaign');
        console.error('Error:', error);
      }
    }
  };

  const handleSendSms = async (campaign: CampaignDTO) => {
    if (campaign.smsSent) {
      toast.error('SMS already sent for this campaign');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/${campaign.id}/send-sms`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          customMessage: campaign.summary || campaign.content?.substring(0, 160)
        })
      });
      
      if (response.ok) {
        toast.success('SMS sent successfully');
        if (aspirantId) fetchCampaigns(aspirantId);
      } else {
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }
        const error = await response.json();
        toast.error(`Failed to send SMS: ${error.error}`);
      }
    } catch (error) {
      toast.error('Error sending SMS');
    }
  };

  // Stat Card Component
  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-xl font-bold text-[#0b2045] mt-1">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (!aspirantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-[#ca8a04] mb-4" />
          <h3 className="text-lg font-semibold text-[#0b2045] mb-2">Authentication Required</h3>
          <p className="text-gray-600 mb-4">Please login as an aspirant to view campaigns.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Dashboard Stats */}
      <div className="bg-gradient-to-r from-[#0b2045] to-[#1a3d6e] rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Campaign Management</h1>
            <p className="text-[#a8d59b] mt-1">Create and manage your SMS campaigns</p>
            
            {/* API Status */}
            {apiWorking !== null && (
              <div className="mt-2">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                  apiWorking ? 'bg-[#3c8d2e]/30 text-[#a8d59b]' : 'bg-[#ca8a04]/30 text-[#fefce8]'
                }`}>
                  API: {apiWorking ? '✅ Connected' : '⚠️ Issues detected'}
                </span>
              </div>
            )}
            
            {/* Ward Info */}
            {aspirantInfo && (
              <div className="mt-4 bg-white/10 p-3 rounded-lg inline-block">
                <div className="flex items-center">
                  <Target className="mr-2" size={16} />
                  <span className="font-medium">Ward:</span>
                  <span className="ml-2">
                    {aspirantInfo.wardName || 'Not assigned'}
                    {aspirantInfo.wardId && ` (ID: ${aspirantInfo.wardId})`}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => aspirantId && fetchAspirantAndCampaigns(aspirantId)}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-white text-[#0b2045] rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              <Plus size={18} className="mr-2" />
              New Campaign
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Campaigns"
          value={dashboardStats.totalCampaigns}
          icon={<MessageSquare className="text-[#0b2045]" size={20} />}
          color="bg-[#0b2045]/10"
        />
        
        <StatCard
          title="Published"
          value={dashboardStats.published}
          icon={<BarChart3 className="text-[#3c8d2e]" size={20} />}
          color="bg-[#3c8d2e]/10"
        />
        
        <StatCard
          title="Drafts"
          value={dashboardStats.draft}
          icon={<MessageSquare className="text-[#ca8a04]" size={20} />}
          color="bg-[#fefce8]"
        />
        
        <StatCard
          title="Scheduled"
          value={dashboardStats.scheduled}
          icon={<Calendar className="text-[#0b2045]" size={20} />}
          color="bg-[#0b2045]/10"
        />
        
        <StatCard
          title="SMS Sent"
          value={dashboardStats.smsSent}
          icon={<Send className="text-[#3c8d2e]" size={20} />}
          color="bg-[#3c8d2e]/10"
        />
        
        <StatCard
          title="Citizens Reached"
          value={dashboardStats.citizensReached.toLocaleString()}
          icon={<Users className="text-[#0b2045]" size={20} />}
          color="bg-[#0b2045]/10"
        />
      </div>

      {/* Campaigns List Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-[#0b2045]">All Campaigns</h2>
            <p className="text-gray-600">Manage and track your SMS campaigns</p>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex space-x-1 border-b border-gray-200">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                filter === 'all'
                  ? 'border-[#3c8d2e] text-[#0b2045]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All ({campaigns.length})
            </button>
            <button
              onClick={() => setFilter('DRAFT')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                filter === 'DRAFT'
                  ? 'border-[#3c8d2e] text-[#0b2045]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Drafts ({dashboardStats.draft})
            </button>
            <button
              onClick={() => setFilter('PUBLISHED')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                filter === 'PUBLISHED'
                  ? 'border-[#3c8d2e] text-[#0b2045]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Published ({dashboardStats.published})
            </button>
            <button
              onClick={() => setFilter('SCHEDULED')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                filter === 'SCHEDULED'
                  ? 'border-[#3c8d2e] text-[#0b2045]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Scheduled ({dashboardStats.scheduled})
            </button>
            <button
              onClick={() => setFilter('SMS_SENT')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                filter === 'SMS_SENT'
                  ? 'border-[#3c8d2e] text-[#0b2045]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              SMS Sent ({dashboardStats.smsSent})
            </button>
          </div>
        </div>

        {/* Campaigns List */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0b2045] mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading campaigns...</p>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-[#0b2045]">No campaigns</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filter === 'all' 
                  ? 'Get started by creating a new campaign.'
                  : `No ${filter.toLowerCase()} campaigns found.`}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-[#3c8d2e] text-white rounded-lg text-sm font-medium hover:bg-[#2d7022] transition-colors"
                >
                  <Plus size={16} className="mr-2" />
                  New Campaign
                </button>
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#f4f7f6]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                    Type & Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCampaigns.map((campaign) => {
                  const status = getStatusDisplay(campaign.status);
                  const costData = calculateCost(campaign);
                  const createdDate = new Date(campaign.createdAt).toLocaleDateString();
                  
                  return (
                    <tr key={campaign.id} className="hover:bg-[#f4f7f6] transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-[#0b2045]">{campaign.campaignName}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {campaign.summary || campaign.description || 'No description'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm text-[#0b2045]">
                            {getTypeDisplay(campaign.type)}
                          </div>
                          <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#0b2045]">
                          {(campaign.smsRecipients || campaign.estimatedRecipients || 0).toLocaleString()}
                        </div>
                        {campaign.targetWardIds && campaign.targetWardIds.length > 0 && (
                          <div className="text-xs text-gray-500">
                            {campaign.targetWardIds.length} ward(s)
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-[#0b2045]">
                          {costData.cost}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#0b2045]">{createdDate}</div>
                        {campaign.scheduledAt && (
                          <div className="text-xs text-gray-500">
                            Scheduled: {new Date(campaign.scheduledAt).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewCampaign(campaign.id)}
                            className="p-1 text-gray-400 hover:text-[#0b2045] transition-colors"
                            title="View Campaign"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditCampaign(campaign.id)}
                            className="p-1 text-gray-400 hover:text-[#3c8d2e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Edit Campaign"
                            disabled={campaign.smsSent}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Delete Campaign"
                            disabled={campaign.smsSent}
                          >
                            <Trash2 size={16} />
                          </button>
                          {!campaign.smsSent && campaign.status === 'PUBLISHED' && (
                            <button
                              onClick={() => handleSendSms(campaign)}
                              className="p-1 text-gray-400 hover:text-[#3c8d2e] transition-colors"
                              title="Send SMS"
                            >
                              <Send size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-[#f4f7f6] border border-[#0b2045]/10 rounded-xl p-5">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-[#0b2045]/10 rounded-full flex items-center justify-center">
              <span className="text-[#0b2045] font-bold">💡</span>
            </div>
          </div>
          <div className="ml-4">
            <h4 className="font-medium text-[#0b2045]">Campaign Tips</h4>
            <ul className="mt-2 text-sm text-[#0b2045]/80 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Keep messages concise for better SMS delivery rates</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Schedule campaigns during peak hours (9-11 AM, 5-7 PM)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Always test your campaign with a small group first</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-[#f4f7f6] border border-[#0b2045]/10 rounded-xl p-4">
          <details>
            <summary className="font-mono text-sm cursor-pointer text-[#0b2045]">Debug Info</summary>
            <div className="mt-4 space-y-2">
              <p className="text-xs font-mono text-[#0b2045]">Aspirant ID: {aspirantId}</p>
              <p className="text-xs font-mono text-[#0b2045]">API Working: {apiWorking ? '✅' : '❌'}</p>
              <p className="text-xs font-mono text-[#0b2045]">Campaigns Count: {campaigns.length}</p>
              <p className="text-xs font-mono text-[#0b2045]">API Base URL: {API_BASE_URL}</p>
              <p className="text-xs font-mono text-[#0b2045]">Token: {localStorage.getItem('token') ? '✅ Present' : '❌ Missing'}</p>
            </div>
          </details>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <CreateCampaignModal 
          onClose={() => setShowCreateModal(false)} 
          onSuccess={() => {
            if (aspirantId) {
              fetchCampaigns(aspirantId);
            }
          }}
        />
      )}
    </div>
  );
};

export default SmsCampaigns;